/**
 * The reference numerals a set of elements shows, projected from the set rather than stored.
 *
 * Registry list order is the numbering order. Overrides occupy first. Every other row takes
 * the next unused term of the convention sequence. A collision is unconstructible: a term
 * leaves the supply the moment it is handed out. A member of a group takes no term: it shows
 * its group's numeral and a letter, from its order among the group's members ("20a", "20b").
 *
 * The same rule runs in `packages/pipeline/src/patent_pipeline/numbering.py`, and both are
 * held to `packages/contracts/fixtures/element-numerals.json`.
 *
 * Structural types rather than the registry's own, so the dependency runs one way: the rules
 * that build a set read the numbers it shows, and the numbers never read the rules.
 */

import { bijectiveBase26 } from "./bijective-base-26";

export const NUMBERING_CONVENTIONS = [
  "even",
  "sequential",
  "fives",
  "tens",
] as const;

export type NumberingConvention = (typeof NUMBERING_CONVENTIONS)[number];

export interface NumberedRow {
  element_id: string;
  numeral_override?: number | null;
  /** Absent on a row stored before sub-categories existed, which is an element. */
  kind?: "element" | "group";
  /** On a member: the group row it belongs to. */
  group_id?: string | null;
}

export interface NumberedSet {
  registry: readonly NumberedRow[];
  /**
   * Optional, and defaulted when absent: a set stored before conventions existed carries
   * no settings, and the browser reads such a set the moment it arrives. Even is the
   * ruling those sets were drawn under.
   */
  settings?: { numbering_convention?: NumberingConvention };
}

/**
 * Even leaves odds unclaimed so an examiner can insert a part later. Sequential has no
 * insertion room. No ceiling: a projected numeral is never stored, so a cap here could only
 * blank the screen on a set too big for the API, which is the moment the attorney needs to
 * see it in order to cut it down.
 */
const SEQUENCE: Record<NumberingConvention, { first: number; step: number }> = {
  even: { first: 10, step: 2 },
  sequential: { first: 1, step: 1 },
  fives: { first: 5, step: 5 },
  tens: { first: 10, step: 10 },
};

export const CONVENTION_CHOICES: ReadonlyArray<{
  value: NumberingConvention;
  label: string;
}> = [
  { value: "even", label: "Even (10, 12, 14)" },
  { value: "sequential", label: "Sequential (1, 2, 3)" },
  { value: "fives", label: "Fives (5, 10, 15)" },
  { value: "tens", label: "Tens (10, 20, 30)" },
];

/**
 * For a set, which group each row is lettered under, or null when it is numbered itself.
 *
 * A row is a member when its group id names a row of kind group in the same set. A group,
 * an element, and a row whose group id names nothing it can belong to are all numbered.
 */
export function membership(
  registry: readonly NumberedRow[],
): (row: NumberedRow) => string | null {
  const groups = new Set(
    registry.filter((row) => row.kind === "group").map((row) => row.element_id),
  );
  return (row) =>
    row.kind !== "group" && row.group_id != null && groups.has(row.group_id)
      ? row.group_id
      : null;
}

/**
 * The numeral each row shows, keyed by element id: "20" for an element or a group, "20a"
 * for a member.
 *
 * Overrides are taken out of the supply before anything is assigned. The cursor only moves
 * forward, so the lowest free term cannot fall, and a four-element even set with the first
 * row overridden to 14 shows 14, 10, 12, 16 rather than colliding on 16.
 *
 * A row is a member when its group id names a row of kind group in this set. Members are
 * passed over by the sequence, and their own overrides are never read, so a group of three
 * costs one term. Any other row is numbered, which includes a row whose group id names
 * nothing it can belong to: the API refuses that set, and the screen still shows it whole.
 */
export function numeralsOf(definitions: NumberedSet): Map<string, string> {
  const groupOf = membership(definitions.registry);

  const numbered = definitions.registry.filter((row) => groupOf(row) === null);
  const taken = new Set<number>();
  for (const row of numbered) {
    if (row.numeral_override != null) taken.add(row.numeral_override);
  }

  const { first, step } =
    SEQUENCE[definitions.settings?.numbering_convention ?? "even"];
  let next = first;
  const assigned = new Map<string, number>();

  for (const row of numbered) {
    if (row.numeral_override != null) {
      assigned.set(row.element_id, row.numeral_override);
      continue;
    }
    while (taken.has(next)) next += step;
    taken.add(next);
    assigned.set(row.element_id, next);
  }

  const lettered = new Map<string, number>();
  const shown = new Map<string, string>();
  for (const row of definitions.registry) {
    const group = groupOf(row);
    if (group === null) {
      shown.set(row.element_id, String(assigned.get(row.element_id)));
      continue;
    }
    // The API refuses a 27th member, but a set it would refuse still shows a numeral on
    // every row ("20aa") rather than blanking the screen.
    const position = (lettered.get(group) ?? 0) + 1;
    lettered.set(group, position);
    shown.set(
      row.element_id,
      `${String(assigned.get(group))}${bijectiveBase26(position)}`,
    );
  }
  return shown;
}
